import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { SignedOutBanner } from "@whoosh/design-system/src/apps/PatronApp/layout/SignedOutBanner";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Text } from "@whoosh/design-system/src/components/primitive";

import { AuthenticationModal } from "../../../features/Authentication/components/AuthenticationModal";
import { useCurrentClubSlug } from "../../../features/Session";
import { useIsUserSignedIn } from "../../../features/Session/hooks/useIsUserSignedIn";
import { useUserBelongsToCurrentClub } from "../../../features/Session/hooks/useUserBelongsToCurrentClub";
import { LessonsSignedOutBanner_session$key } from "./__generated__/LessonsSignedOutBanner_session.graphql";

type Props = {
  sessionRef: LessonsSignedOutBanner_session$key | null;
  completeCallback: () => void;
  cancelCallback: () => void;
  authModalState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
};
export const LessonsSignedOutBanner = ({
  completeCallback,
  cancelCallback,
  sessionRef,
  authModalState,
}: Props) => {
  const isMobile = useResponsiveProp({ base: 1, lg: 0 });
  const [isAuthModalOpen, setIsAuthModalOpen] = authModalState;

  const session = useFragment(LessonsSignedOutBannerFragmentString, sessionRef);

  const currentClubSlug = useCurrentClubSlug(session ?? null);
  const isSignedIn = useIsUserSignedIn(session ?? null);
  const userBelongsToClub = useUserBelongsToCurrentClub(session ?? null);

  const title = isSignedIn
    ? t`Join now to avoid missing your times.`
    : t`Sign in to avoid missing your times.`;

  const subtitle = isSignedIn
    ? t`You have not joined this club yet.`
    : t`You are signed out`;

  const buttonText = isSignedIn ? t`Join Club` : t`Sign In`;

  const showBanner = !isSignedIn || !userBelongsToClub;

  return (
    <>
      <SignedOutBanner
        position={isMobile ? "bottom" : "top"}
        isOpen={showBanner}
        title={title}
        subTitle={subtitle}
        actionButton={
          <Button
            accessibilityLabel={buttonText}
            variant="white"
            size="tiny"
            onPress={() => {
              setIsAuthModalOpen(true);
            }}
          >
            <Text variant="mainTiny" color="text-primary">
              {buttonText}
            </Text>
          </Button>
        }
      />
      <AuthenticationModal
        isOpen={isAuthModalOpen}
        onClose={cancelCallback}
        onComplete={completeCallback}
        clubSlug={currentClubSlug}
      />
    </>
  );
};

const LessonsSignedOutBannerFragmentString = graphql`
  fragment LessonsSignedOutBanner_session on Session {
    ...useIsUserSignedIn_session
    ...useUserBelongsToCurrentClub_session
    ...useCurrentClubSlug_session
  }
`;
